import React, { useRef, useState, useEffect } from 'react';
import '../css/about.css';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import proclaimationVideo from '../assets/proclaimation.mp4';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api').replace(/\/+$/, '');

export default function AboutDRY({ showReadMore = false }) {
    const navigate = useNavigate();
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [data, setData] = useState({
        subtitle: 'Our Foundation',
        title: 'The Birth of a Sovereign Nation',
        description1: 'The Democratic Republic of the Yoruba (D.R.Y) represents the fulfillment of a divine mandate and the ancestral dream of the Yoruba people. For the first time in history, a nation has emerged out of Africa peacefully, without conflict or bloodshed.',
        description2: 'Guided by the vision of the Mother of the Indigenous Yoruba People (I.Y.P), Mama Modupe Onitiri-Abiola, we stand as a beacon of hope, prosperity, and restored heritage for the entire black race.',
        milestone1Date: 'November 20, 2022',
        milestone1Title: 'Declaration of Independence',
        milestone1Desc: 'The D.R.Y formally declared its independence to the world.',
        milestone2Date: 'April 12, 2024',
        milestone2Title: 'Proclamation of Sovereignty',
        milestone2Desc: 'Full sovereignty was proclaimed, marking the birth of the 55th nation in Africa.',
        videoSource: proclaimationVideo,
        poster: "https://via.placeholder.com/600x400?text=Proclamation+of+Sovereignty"
    });

    useEffect(() => {
        const load = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/proclamation`);
                if (res.data) {
                    let vSrc = res.data.videoSource || proclaimationVideo;
                    // Append timestamp to force refresh if it's a URL
                    if (vSrc && vSrc.startsWith('http')) {
                        vSrc = `${vSrc}?t=${Date.now()}`;
                    }
                    
                    setData(prev => ({
                        ...prev,
                        ...res.data,
                        videoSource: vSrc,
                        poster: res.data.poster || "https://via.placeholder.com/600x400?text=Proclamation+of+Sovereignty"
                    }));
                    
                    // Force reload video element if ref exists
                    if (videoRef.current) {
                        videoRef.current.load();
                    }
                }
            } catch (err) {
                console.error("Failed to load proclamation data", err);
            }
        };
        load();
    }, []);

    const handlePlayPause = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const handleVideoEnd = () => {
        setIsPlaying(false);
    };

    return (
        <section className="about-section">
            <div className="container">
                <div className="row align-items-center">
                    <div className="col-lg-6">
                        <div className="about-title-wrapper">
                            <span className="about-subtitle">{data.subtitle}</span>
                            <h2 className="about-heading">{data.title}</h2>
                        </div>
                        
                        <div className="about-content">
                            <p>{data.description1}</p>
                            <p>{data.description2}</p>
                            
                            <div className="milestones-wrapper">
                                <div className="milestone-item">
                                    <span className="milestone-date">{data.milestone1Date}</span>
                                    <h5 className="milestone-title">{data.milestone1Title}</h5>
                                    <p className="small text-muted">{data.milestone1Desc}</p>
                                </div>
                                <div className="milestone-item">
                                    <span className="milestone-date">{data.milestone2Date}</span>
                                    <h5 className="milestone-title">{data.milestone2Title}</h5>
                                    <p className="small text-muted">{data.milestone2Desc}</p>
                                </div>
                            </div>

                            {showReadMore && (
                                <button onClick={() => navigate('/about')} className="btn btn-primary mt-4">
                                    Read More
                                </button>
                            )}
                        </div>
                    </div>
                    
                    <div className="col-lg-6">
                        <div className="about-image-wrapper">
                            <div className="video-wrapper" onClick={handlePlayPause}>
                                <div className={`video-overlay ${isPlaying ? 'hidden' : ''}`}>
                                    <div className="play-button">
                                        <i className="fas fa-play"></i>
                                    </div>
                                </div>
                                <video 
                                    ref={videoRef}
                                    key={data.videoSource}
                                    src={data.videoSource}
                                    className="about-video" 
                                    poster={data.poster}
                                    onEnded={handleVideoEnd}
                                    onPause={() => setIsPlaying(false)}
                                    onPlay={() => setIsPlaying(true)}
                                >
                                    Your browser does not support the video tag.
                                </video>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
